import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Business as TenantIcon,
  Dns as ServerIcon,
  Devices as AgentIcon,
  Memory as CpuIcon,
  Storage as DiskIcon,
  Timer as UptimeIcon,
  Refresh,
} from '@mui/icons-material';
import api from '../../services/api';

interface TenantSummary {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  timezone: string;
}

interface ServerSummary {
  id: number;
  name: string;
  server_id: string;
  role: string;
  status: string;
  url: string;
}

interface SystemHealth {
  cpu: { percent: number | null; load_avg_1m: number; load_avg_5m: number; load_avg_15m: number };
  memory: { total_mb: number; used_mb: number; available_mb: number; percent: number };
  disk: { total_gb: number; used_gb: number; free_gb: number; percent: number };
  uptime: { seconds: number; display: string };
}

function UsageBar({ value, label, color }: { value: number; label: string; color: 'primary' | 'warning' | 'error' | 'success' }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" fontWeight={600}>{value}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={value} color={color}
        sx={{ height: 8, borderRadius: 4 }} />
    </Box>
  );
}

function barColor(pct: number): 'success' | 'primary' | 'warning' | 'error' {
  if (pct < 50) return 'success';
  if (pct < 75) return 'primary';
  if (pct < 90) return 'warning';
  return 'error';
}

const REFRESH_INTERVAL = 30000;

export default function AdminDashboardPage() {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [servers, setServers] = useState<ServerSummary[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [sysHealth, setSysHealth] = useState<SystemHealth | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(() => {
    Promise.all([
      api.get('/api/tenants').then(r => setTenants(r.data)).catch(() => {}),
      api.get('/api/servers').then(r => setServers(r.data)).catch(() => {}),
      api.get('/api/health').then(r => setHealth(r.data)).catch(() => {}),
      api.get('/api/health/system').then(r => setSysHealth(r.data)).catch(() => {}),
    ]).then(() => {
      setLastUpdated(new Date());
    });
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [load]);

  const activeTenants = tenants.filter(t => t.is_active).length;
  const onlineServers = servers.filter(s => s.status === 'online').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">System Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button startIcon={<Refresh />} onClick={load} variant="outlined" size="small">Refresh</Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TenantIcon sx={{ fontSize: 36, color: 'primary.main', mb: 0.5 }} />
              <Typography variant="h3">{activeTenants}</Typography>
              <Typography variant="body2" color="text.secondary">Active Tenants</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ServerIcon sx={{ fontSize: 36, color: 'success.main', mb: 0.5 }} />
              <Typography variant="h3">{onlineServers}/{servers.length}</Typography>
              <Typography variant="body2" color="text.secondary">Servers Online</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AgentIcon sx={{ fontSize: 36, color: 'info.main', mb: 0.5 }} />
              <Typography variant="h3">{health?.connected_agents ?? '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Connected Agents</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <UptimeIcon sx={{ fontSize: 36, color: 'secondary.main', mb: 0.5 }} />
              <Typography variant="h4">{sysHealth?.uptime.display ?? '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Uptime</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {sysHealth && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CpuIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={600}>CPU</Typography>
                </Box>
                <UsageBar value={sysHealth.cpu.percent ?? 0} label="Utilization" color={barColor(sysHealth.cpu.percent ?? 0)} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Load: {sysHealth.cpu.load_avg_1m} / {sysHealth.cpu.load_avg_5m} / {sysHealth.cpu.load_avg_15m}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CpuIcon color="warning" />
                  <Typography variant="subtitle1" fontWeight={600}>Memory</Typography>
                </Box>
                <UsageBar value={sysHealth.memory.percent} label={`${sysHealth.memory.used_mb} MB / ${sysHealth.memory.total_mb} MB`} color={barColor(sysHealth.memory.percent)} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Available: {sysHealth.memory.available_mb} MB
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DiskIcon color="info" />
                  <Typography variant="subtitle1" fontWeight={600}>Disk</Typography>
                </Box>
                <UsageBar value={sysHealth.disk.percent} label={`${sysHealth.disk.used_gb} GB / ${sysHealth.disk.total_gb} GB`} color={barColor(sysHealth.disk.percent)} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Free: {sysHealth.disk.free_gb} GB
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Tenants</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Timezone</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell>{t.slug}</TableCell>
                    <TableCell>{t.timezone}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={t.is_active ? <OnlineIcon /> : <OfflineIcon />}
                        label={t.is_active ? 'Active' : 'Inactive'}
                        color={t.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No tenants</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Cloud Servers</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servers.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.role} color={s.role === 'primary' ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.url}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={s.status === 'online' ? <OnlineIcon /> : <OfflineIcon />}
                        label={s.status}
                        color={s.status === 'online' ? 'success' : s.status === 'degraded' ? 'warning' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {servers.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No servers configured</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
}
